import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, ArrowRight, Lock, Loader2, Star, Target, Zap, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const safeArray = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val.map(item => typeof item === 'string' ? item : item?.title || item?.name || String(item));
  if (typeof val === 'string') return val.split(',').map(s => s.trim()).filter(Boolean);
  return [];
};

const MiniResults = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useLanguage();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/quick-quiz/profile`);
      setProfile(res.data.analysis);
    } catch (e) {
      console.warn('Mini profile fetch:', e.message);
      navigate('/quick-quiz');
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const hasPaid = user?.purchases?.includes('diagnostic');
  const fr = language === 'fr';

  if (loading) return (
    <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
    </div>
  );

  if (!profile) return null;

  const strengths = safeArray(profile.top_strengths);
  const careers = safeArray(profile.career_glimpse);

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Animated header */}
      <div className="bg-gradient-to-br from-[#FF6B6B] via-[#FFE66D] to-[#4ECDC4] px-4 py-12 text-center">
        <div className="max-w-md mx-auto">
          <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-serif text-2xl sm:text-3xl font-bold text-white mb-2" data-testid="mini-results-title">
            {fr ? 'Ton Ikigai commence à se dessiner' : 'Your Ikigai is taking shape'}
          </h1>
          <p className="text-white/80 text-sm">
            {fr ? 'Voici un premier aperçu de qui tu es vraiment.' : 'Here\'s a first glimpse of who you really are.'}
          </p>
        </div>
      </div>

      <main className="max-w-md mx-auto px-4 -mt-6">
        {/* Personality snapshot */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-stone-100 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Heart className="w-4 h-4 text-[#FF6B6B]" />
            <h3 className="font-serif font-semibold text-stone-900">
              {fr ? 'Ta personnalité' : 'Your personality'}
            </h3>
          </div>
          <p className="text-stone-600 text-sm leading-relaxed" data-testid="personality-snapshot">
            {profile.personality_snapshot || ''}
          </p>
        </div>

        {/* Strengths */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-stone-100 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="w-4 h-4 text-[#FFE66D]" />
            <h3 className="font-serif font-semibold text-stone-900">
              {fr ? 'Tes 3 forces clés' : 'Your 3 key strengths'}
            </h3>
          </div>
          <div className="space-y-2">
            {strengths.map((s, i) => (
              <div key={`str-${i}-${s.slice(0,8)}`} className="flex items-start gap-2">
                <Star className="w-4 h-4 text-[#FFE66D] mt-0.5 flex-shrink-0" />
                <span className="text-sm text-stone-600">{s}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Career glimpse — BLURRED with teaser */}
        <div className="relative mb-4">
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-stone-100">
            <div className="flex items-center gap-2 mb-3">
              <Target className="w-4 h-4 text-[#4ECDC4]" />
              <h3 className="font-serif font-semibold text-stone-900">
                {fr ? 'Tes pistes de carrière' : 'Your career paths'}
              </h3>
            </div>

            {/* First career: show title, blur the rest */}
            {careers.length > 0 && (
              <div className="bg-[#4ECDC4]/5 rounded-xl p-3 border border-[#4ECDC4]/10 mb-2">
                <span className="text-sm text-stone-700">
                  {careers[0].length > 60 ? careers[0].slice(0, 60) + '...' : careers[0]}
                </span>
              </div>
            )}

            {/* Remaining careers: fully blurred */}
            <div style={{ filter: 'blur(6px)', userSelect: 'none', pointerEvents: 'none' }}>
              {careers.slice(1).map((c, i) => (
                <div key={`bcar-${i}`} className="bg-[#4ECDC4]/5 rounded-xl p-3 border border-[#4ECDC4]/10 mb-2">
                  <span className="text-sm text-stone-700">{c}</span>
                </div>
              ))}
              <div className="bg-[#4ECDC4]/5 rounded-xl p-3 border border-[#4ECDC4]/10 mb-2">
                <span className="text-sm text-stone-700">{fr ? 'Troisième piste de carrière personnalisée basée sur tes talents uniques et tes aspirations profondes' : 'Third personalized career path based on your unique talents and deep aspirations'}</span>
              </div>
              <div className="bg-[#4ECDC4]/5 rounded-xl p-3 border border-[#4ECDC4]/10 mb-2">
                <span className="text-sm text-stone-700">{fr ? 'Quatrième piste avec projets concrets à lancer et environnement de travail idéal' : 'Fourth path with concrete projects to launch and ideal work environment'}</span>
              </div>
              <div className="bg-[#4ECDC4]/5 rounded-xl p-3 border border-[#4ECDC4]/10">
                <span className="text-sm text-stone-700">{fr ? 'Cinquième piste incluant le coaching IA quotidien personnalisé pendant 90 jours' : 'Fifth path including personalized daily AI coaching for 90 days'}</span>
              </div>
            </div>
          </div>

          {/* Lock overlay on blurred part */}
          <div className="absolute bottom-0 left-0 right-0 h-[70%] flex items-end justify-center pb-6 bg-gradient-to-t from-white via-white/90 to-transparent rounded-b-2xl">
            <div className="text-center">
              <Lock className="w-6 h-6 text-[#FF6B6B] mx-auto mb-2" />
              <p className="text-stone-900 font-serif font-semibold text-sm mb-1">
                {fr ? '+ 3 carrières, projets concrets, coaching IA...' : '+ 3 careers, concrete projects, AI coaching...'}
              </p>
              <Button
                onClick={() => navigate(hasPaid ? '/results' : '/payment')}
                className="btn-primary flex items-center gap-2 mt-2"
                data-testid="unlock-full-btn"
              >
                <Sparkles className="w-4 h-4" />
                {hasPaid 
                  ? (fr ? 'Voir mon profil complet' : 'View my full profile')
                  : (fr ? 'Tout débloquer pour 1,99€' : 'Unlock all for €1.99')}
              </Button>
              {!hasPaid && (
                <span className="text-xs text-stone-400 block mt-1">
                  {fr ? 'Paiement unique. Accès à vie.' : 'One-time. Lifetime access.'}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Ikigai hint */}
        <div className="bg-gradient-to-br from-[#FF6B6B]/5 to-[#4ECDC4]/5 rounded-2xl p-6 border border-stone-100 mb-6 text-center">
          <p className="font-serif text-lg font-semibold text-stone-900 italic" data-testid="ikigai-hint">
            "{profile.ikigai_hint || ''}"
          </p>
        </div>

        {/* Secondary: Continue with full modules */}
        <div className="text-center pb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-sm text-stone-400 hover:text-stone-600 underline transition-colors"
            data-testid="go-dashboard-btn"
          >
            {fr ? 'Retour au tableau de bord' : 'Back to dashboard'}
          </button>
        </div>
      </main>
    </div>
  );
};

export default MiniResults;
